import React, { useEffect, useState } from 'react';
import { useClient } from '../context/ClientContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Download, FileText, Upload, ArrowDownToLine, ArrowUpFromLine, Loader2, BookOpen, FileSpreadsheet, AlertCircle } from 'lucide-react';

const VOUCHER_TYPES = [
  { key: 'sales',    label: 'Sales',    desc: 'One voucher per POS day (split Cash / Card / GST)' },
  { key: 'purchase', label: 'Purchase', desc: 'One per supplier invoice' },
  { key: 'payment',  label: 'Payment',  desc: 'One per expense row (Rent, Utilities, etc.)' },
  { key: 'journal',  label: 'Journal',  desc: 'Reconciliation variance adjustments (short batches / over-credits)' },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const sixMonthsAgoISO = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return d.toISOString().slice(0, 10);
};

export const Tally = () => {
  const { selectedClient, API } = useClient();
  const [range, setRange] = useState({ start: sixMonthsAgoISO(), end: todayISO() });
  const [selectedTypes, setSelectedTypes] = useState({
    sales: true, purchase: true, payment: true, journal: true,
  });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const loadPreview = async () => {
    if (!selectedClient) return;
    setPreviewing(true);
    try {
      const res = await axios.get(`${API}/tally/preview/${selectedClient.id}`, {
        params: { start: range.start, end: range.end },
      });
      setPreview(res.data);
    } catch (e) {
      setPreview({ error: e?.response?.data?.detail || e.message });
    } finally {
      setPreviewing(false);
    }
  };

  useEffect(() => { loadPreview(); /* eslint-disable-next-line */ }, [selectedClient, range.start, range.end]);

  if (!selectedClient) {
    return <div className="text-center text-text-secondary py-12">Select a client.</div>;
  }

  const downloadFile = async (which) => {
    const url = which === 'handoff'
      ? `${API}/tally/handoff/${selectedClient.id}?start=${range.start}&end=${range.end}`
      : `${API}/tally/export/${selectedClient.id}?start=${range.start}&end=${range.end}&types=${Object.keys(selectedTypes).filter(k => selectedTypes[k]).join(',')}`;
    setDownloading(which);
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`${res.status} ${body}`);
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const match = /filename="([^"]+)"/.exec(cd);
      const filename = match ? match[1] : `tally_${which}.${which === 'handoff' ? 'pdf' : 'zip'}`;
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(a.href);
    } catch (e) {
      alert('Download failed: ' + e.message);
    } finally {
      setDownloading(null);
    }
  };

  const someSelected = Object.values(selectedTypes).some(Boolean);

  return (
    <div className="space-y-6" data-testid="tally-page">
      {/* Header */}
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-text-secondary font-semibold mb-1">Tally Integration</p>
        <h1 className="text-3xl font-bold text-primary flex items-center gap-2">
          <BookOpen className="h-7 w-7" /> Tally — {selectedClient.name}
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          File-based two-way bridge with your Tally Prime / Tally ERP 9. No software install required.
        </p>
      </div>

      {/* Two sections side-by-side */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* LEFT — Pull from Tally */}
        <div className="bg-white border border-border rounded-md p-5">
          <div className="flex items-start gap-3 mb-4">
            <div className="rounded-md p-2.5 bg-emerald-50 text-emerald-900 border border-emerald-200">
              <ArrowDownToLine className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-primary">Pull from Tally</h2>
              <p className="text-xs text-text-secondary mt-0.5">Export reports from Tally → upload to Epicurea (our AI parses them).</p>
            </div>
          </div>

          <div className="space-y-3 text-sm">
            <p className="text-text-secondary text-xs leading-relaxed">
              Tally's Day Book / Sales Register / Purchase Register exports already work via the global <strong>Smart Upload Zone</strong>.
              The AI extractor recognizes the format automatically. Here are the exports we recommend:
            </p>

            <ExportInstruction
              icon={<FileSpreadsheet className="h-4 w-4" />}
              title="Day Book (any period)"
              path={<>Gateway of Tally → Display More Reports → <strong>Day Book</strong> → <kbd>Alt + E</kbd> (Export) → <strong>Excel</strong></>}
              maps="→ feeds Revenue, Expenses, and Reconciliation"
            />
            <ExportInstruction
              icon={<FileSpreadsheet className="h-4 w-4" />}
              title="Sales Voucher Register"
              path={<>Gateway → Display More Reports → Accounts Books → <strong>Sales Register</strong> → <kbd>Alt + E</kbd> → Excel</>}
              maps="→ feeds Revenue (daily POS sales)"
            />
            <ExportInstruction
              icon={<FileSpreadsheet className="h-4 w-4" />}
              title="Purchase Voucher Register"
              path={<>Gateway → Display More Reports → Accounts Books → <strong>Purchase Register</strong> → <kbd>Alt + E</kbd> → Excel</>}
              maps="→ feeds Supplier Invoices"
            />
            <ExportInstruction
              icon={<FileSpreadsheet className="h-4 w-4" />}
              title="Trial Balance"
              path={<>Gateway → Display More Reports → <strong>Trial Balance</strong> → <kbd>Alt + E</kbd> → Excel</>}
              maps="→ feeds Financial Analysis & P&L"
            />
            <ExportInstruction
              icon={<FileSpreadsheet className="h-4 w-4" />}
              title="Stock Summary"
              path={<>Gateway → Display More Reports → Inventory Books → <strong>Stock Summary</strong> → <kbd>Alt + E</kbd> → Excel</>}
              maps="→ feeds Inventory / cost validation"
            />

            <a
              href="/files"
              className="block mt-4 bg-primary text-white text-center py-2.5 rounded-md hover:bg-primary-hover transition-colors text-sm font-semibold"
              data-testid="tally-go-upload"
            >
              <Upload className="h-4 w-4 inline-block mr-2" /> Go to Files → drop the exported Excel
            </a>
          </div>
        </div>

        {/* RIGHT — Push to Tally */}
        <div className="bg-white border border-border rounded-md p-5">
          <div className="flex items-start gap-3 mb-4">
            <div className="rounded-md p-2.5 bg-violet-50 text-violet-900 border border-violet-200">
              <ArrowUpFromLine className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-primary">Push to Tally</h2>
              <p className="text-xs text-text-secondary mt-0.5">Download a Tally-compatible XML voucher bundle for your accountant.</p>
            </div>
          </div>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">From</label>
              <input
                type="date" value={range.start}
                onChange={e => setRange({ ...range, start: e.target.value })}
                className="w-full border border-border rounded-md px-2 py-1.5 text-sm"
                data-testid="tally-range-start"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">To</label>
              <input
                type="date" value={range.end}
                onChange={e => setRange({ ...range, end: e.target.value })}
                className="w-full border border-border rounded-md px-2 py-1.5 text-sm"
                data-testid="tally-range-end"
              />
            </div>
          </div>

          {/* Quick presets */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Button size="sm" variant="outline" onClick={() => {
              const d = new Date(); d.setMonth(d.getMonth() - 1);
              setRange({ start: d.toISOString().slice(0,10), end: todayISO() });
            }} data-testid="tally-quick-1m">Last 30D</Button>
            <Button size="sm" variant="outline" onClick={() => {
              const d = new Date(); d.setMonth(d.getMonth() - 3);
              setRange({ start: d.toISOString().slice(0,10), end: todayISO() });
            }} data-testid="tally-quick-3m">Last 90D</Button>
            <Button size="sm" variant="outline" onClick={() => setRange({ start: '2026-01-01', end: '2026-06-30' })} data-testid="tally-quick-99roads">99 Roads Jan–Jun</Button>
          </div>

          {/* Voucher type checkboxes */}
          <p className="text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-2">Include voucher types</p>
          <div className="space-y-2 mb-4">
            {VOUCHER_TYPES.map(t => (
              <label key={t.key} className="flex items-start gap-2 cursor-pointer p-2 rounded border border-border hover:bg-background-secondary" data-testid={`tally-toggle-${t.key}`}>
                <input
                  type="checkbox"
                  checked={selectedTypes[t.key]}
                  onChange={e => setSelectedTypes({ ...selectedTypes, [t.key]: e.target.checked })}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold">{t.label}</span>
                    <span className="text-xs text-text-muted">
                      {preview && !preview.error ? `${preview.counts[t.key]?.toLocaleString() || 0} vouchers` : '…'}
                    </span>
                  </div>
                  <p className="text-[11px] text-text-secondary">{t.desc}</p>
                </div>
              </label>
            ))}
          </div>

          {/* Preview banner */}
          {previewing && (
            <div className="bg-background-secondary p-3 rounded text-xs text-text-secondary mb-3 flex items-center gap-2" data-testid="tally-preview-loading">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Counting vouchers…
            </div>
          )}
          {preview && !preview.error && !previewing && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 p-3 rounded text-xs mb-3" data-testid="tally-preview-summary">
              <strong>Preview:</strong> {(preview.counts.sales + preview.counts.purchase + preview.counts.payment + preview.counts.journal).toLocaleString()} vouchers total
              · Revenue ₹{preview.totals.sales_revenue.toLocaleString()}
              · Purchases ₹{preview.totals.purchase_amount.toLocaleString()}
              · Expenses ₹{preview.totals.expense_amount.toLocaleString()}
            </div>
          )}
          {preview?.error && (
            <div className="bg-red-50 border border-state-error/30 text-state-error p-3 rounded text-xs mb-3">{preview.error}</div>
          )}

          {/* Download buttons */}
          <div className="grid grid-cols-1 gap-2">
            <Button
              onClick={() => downloadFile('bundle')}
              disabled={!someSelected || downloading === 'bundle'}
              className="w-full bg-primary hover:bg-primary-hover"
              data-testid="tally-download-bundle"
            >
              {downloading === 'bundle'
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Building bundle…</>
                : <><Download className="h-4 w-4 mr-2" /> Download Tally Bundle (.zip)</>}
            </Button>
            <Button
              onClick={() => downloadFile('handoff')}
              disabled={downloading === 'handoff'}
              variant="outline"
              className="w-full"
              data-testid="tally-download-handoff"
            >
              {downloading === 'handoff'
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</>
                : <><FileText className="h-4 w-4 mr-2" /> Accountant Handoff PDF</>}
            </Button>
          </div>
        </div>
      </div>

      {/* How Tally import works — instructions */}
      <div className="bg-white border border-border rounded-md p-5" data-testid="tally-import-steps">
        <h2 className="text-base font-semibold text-primary mb-3">How your accountant imports into Tally</h2>
        <ol className="space-y-2 text-sm">
          <Step n={1}>Open <strong>Tally Prime</strong> and select your company (must match the client name on Epicurea, or rename in Tally).</Step>
          <Step n={2}>Press <kbd>Alt + O</kbd> to open the Import menu.</Step>
          <Step n={3}>Choose <strong>Masters → Vouchers</strong>.</Step>
          <Step n={4}>Browse to one of the XML files in the downloaded bundle (e.g. <code>99_Roads_Sales_…xml</code>) and click <strong>Import</strong>.</Step>
          <Step n={5}>Repeat for each XML in any order. If any ledger doesn't exist yet, Tally will create it on the fly — accept or remap as needed.</Step>
        </ol>
        <p className="text-xs text-text-muted mt-3">
          Every voucher is fully <strong>double-entry balanced</strong> (sum of debits = credits) and carries a <strong>Reference</strong> back to its source (POS Z-ref, Invoice #, or UTR) — so the audit trail is preserved.
        </p>
      </div>

      {/* Future: direct integration hint */}
      <div className="bg-slate-50 border border-slate-200 text-slate-700 rounded-md p-4 text-xs flex items-start gap-2" data-testid="tally-future-note">
        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div>
          <strong>Coming later:</strong> a tiny "Tally Bridge" local agent for clients who want automatic two-way sync.
          For now, the file-based bundle above is the cleanest path — works for every Tally version, no IT changes needed.
        </div>
      </div>
    </div>
  );
};

const ExportInstruction = ({ icon, title, path, maps }) => (
  <div className="bg-background-secondary rounded p-3 border border-border" data-testid={`tally-export-${title.toLowerCase().replace(/[^a-z]/g, '-')}`}>
    <div className="flex items-center gap-2 mb-1">
      <span className="text-primary">{icon}</span>
      <span className="font-semibold text-sm">{title}</span>
    </div>
    <p className="text-xs text-text-secondary leading-relaxed">{path}</p>
    <p className="text-[11px] text-emerald-700 font-medium mt-1">{maps}</p>
  </div>
);

const Step = ({ n, children }) => (
  <li className="flex gap-3">
    <span className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white text-xs font-bold">{n}</span>
    <span className="text-text-primary">{children}</span>
  </li>
);

export default Tally;
